import { signInEmail } from '@/lib/auth-client';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@iq/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useRouter } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

export function SignInForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setPending(true);
    const result = await signInEmail({ email, password });
    setPending(false);
    if (result.ok) {
      await queryClient.invalidateQueries({ queryKey: ['auth', 'session'] });
      await router.invalidate();
      navigate({ to: '/chat' });
    } else {
      setError(result.message);
    }
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>{t('auth.signIn.title')}</CardTitle>
        <CardDescription>{t('auth.signIn.subtitle')}</CardDescription>
      </CardHeader>
      <form onSubmit={onSubmit} data-testid="sign-in-form">
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="signin-email">{t('auth.signIn.email')}</Label>
            <Input
              id="signin-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="signin-password">{t('auth.signIn.password')}</Label>
            <Input
              id="signin-password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          {error ? (
            <p role="alert" className="text-sm text-destructive" data-testid="sign-in-error">
              {error}
            </p>
          ) : null}
        </CardContent>
        <CardFooter className="flex flex-col gap-3">
          <Button type="submit" disabled={pending} className="w-full">
            {pending ? t('auth.signIn.submitting') : t('auth.signIn.submit')}
          </Button>
          <p className="text-sm text-muted-foreground">
            {t('auth.signIn.noAccount')}{' '}
            <Link to="/signup" className="underline">
              {t('auth.signIn.signUpLink')}
            </Link>
          </p>
        </CardFooter>
      </form>
    </Card>
  );
}
